import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IQuestionState } from './Questions.interface';
import {
  getQuestiosForm,
  associateQuestionsToIndicator,
} from 'src/services/KPI/Questions/Questions.service';
import { IKPIFormField } from '../KPIForm/KPIForm.interface';

/**
 * The initial state for the Question slice.
 *
 * @constant
 * @type {IQuestionState}
 */
const initialState: IQuestionState = {
  questions: [],
  associatedQuestion: {
    indicatorId: null,
    periodId: null,
    quarterId: null,
    parameterId: null,
    question: null,
  },
  error: null,
  loading: false,
};

/**
 * A slice of the Redux store responsible for managing question data related to KPIs.
 *
 * This slice contains actions and reducers to control the loading state,
 * manage associated questions, and handle API responses for retrieving questions
 * and associating them with indicators.
 *
 * @constant
 * @type {Slice<IQuestionState>}
 */
const QuestionSlice = createSlice({
  name: 'questions',
  initialState,
  reducers: {
    /**
     * Sets the associated question data based on the provided payload.
     *
     * @param {IQuestionState} state - The current state of the Question slice.
     * @param {PayloadAction<IQuestionState['associatedQuestion']>} action - The action containing the new associated question data.
     */
    setAssociatedQuestion: (
      state,
      action: PayloadAction<IQuestionState['associatedQuestion']>,
    ) => {
      state.associatedQuestion = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        getQuestiosForm.fulfilled,
        (state, action: PayloadAction<IKPIFormField[]>) => {
          state.loading = false;
          state.questions = action.payload;
        },
      )
      .addCase(
        associateQuestionsToIndicator.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        },
      );
  },
});

/**
 * Action creators generated from the Question slice.
 *
 * @constant
 * @type {{ setAssociatedQuestion: (associatedQuestion: IQuestionState['associatedQuestion']) => PayloadAction<IQuestionState['associatedQuestion']> }}
 */
export const { setAssociatedQuestion } = QuestionSlice.actions;

/**
 * The reducer for the Question slice, to be used in the Redux store.
 *
 * @constant
 * @type {Reducer<IQuestionState>}
 */
export default QuestionSlice.reducer;
